import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Mail, Clock, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone || !formData.subject || !formData.message) {
      toast.error("Please fill in all fields");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      toast.success("Message sent successfully!");
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error("Failed to send message. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pt-20" data-testid="contact-page">
      {/* Header */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-[#141414] border-b border-white/10">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="font-outfit text-4xl sm:text-5xl text-white mb-4">
            Contact <span className="gold-gradient">Us</span>
          </h1>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Have questions? We'd love to hear from you. Send us a message and we'll respond as soon as possible.
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div className="animate-fade-in">
              <h2 className="font-outfit text-2xl text-white mb-8">Get in Touch</h2>
              
              <div className="space-y-6 mb-10">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-sm bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0">
                    <MapPin className="text-[#D4AF37]" size={24} />
                  </div>
                  <div>
                    <h3 className="font-outfit text-white mb-1">Our Location</h3>
                    <p className="text-gray-400">
                      3175 W Broward Blvd<br />
                      Fort Lauderdale, FL 33312
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-sm bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0">
                    <Mail className="text-[#D4AF37]" size={24} />
                  </div>
                  <div>
                    <h3 className="font-outfit text-white mb-1">Email Us</h3>
                    <a href="mailto:orders@velvetpress.us" className="text-gray-400 hover:text-[#D4AF37] transition-colors">
                      orders@velvetpress.us
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-sm bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0">
                    <Clock className="text-[#D4AF37]" size={24} />
                  </div>
                  <div>
                    <h3 className="font-outfit text-white mb-1">Business Hours</h3>
                    <p className="text-gray-400">
                      Monday - Friday: 9:00 AM - 6:00 PM<br />
                      Saturday - Sunday: Closed
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="animate-fade-in" style={{ animationDelay: "0.2s" }}>
              <div className="bg-[#141414] p-8 rounded-sm border border-white/10">
                <h2 className="font-outfit text-2xl text-white mb-6">
                  {submitted ? "Thank You!" : "Send Us a Message"}
                </h2>

                {submitted ? (
                  <div className="text-center py-10">
                    <div className="w-16 h-16 rounded-full bg-[#D4AF37]/20 flex items-center justify-center mx-auto mb-4">
                      <Check size={32} className="text-[#D4AF37]" />
                    </div>
                    <p className="text-gray-300 mb-4">
                      We've received your message and will get back to you shortly.
                    </p>
                    <Button
                      onClick={() => {
                        setSubmitted(false);
                        setFormData({ name: "", email: "", phone: "", subject: "", message: "" });
                      }}
                      className="btn-outline-gold"
                    >
                      Send Another Message
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <Label htmlFor="name" className="text-gray-300 mb-2 block">
                          Full Name *
                        </Label>
                        <Input
                          id="name"
                          type="text"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          className="input-dark w-full"
                          placeholder="Your name"
                          data-testid="contact-name"
                        />
                      </div>
                      <div>
                        <Label htmlFor="email" className="text-gray-300 mb-2 block">
                          Email Address *
                        </Label>
                        <Input
                          id="email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          className="input-dark w-full"
                          placeholder="Your email"
                          data-testid="contact-email"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <Label htmlFor="phone" className="text-gray-300 mb-2 block">
                          Phone Number *
                        </Label>
                        <Input
                          id="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          className="input-dark w-full"
                          placeholder="Your phone"
                          data-testid="contact-phone"
                        />
                      </div>
                      <div>
                        <Label htmlFor="subject" className="text-gray-300 mb-2 block">
                          Subject *
                        </Label>
                        <Input
                          id="subject"
                          type="text"
                          value={formData.subject}
                          onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                          className="input-dark w-full"
                          placeholder="Message subject"
                          data-testid="contact-subject"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="message" className="text-gray-300 mb-2 block">
                        Message *
                      </Label>
                      <Textarea
                        id="message"
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        className="input-dark w-full min-h-[150px]"
                        placeholder="How can we help you?"
                        data-testid="contact-message"
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={submitting}
                      className="w-full btn-gold"
                      data-testid="contact-submit-btn"
                    >
                      {submitting ? "Sending..." : "Send Message"}
                    </Button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
